// src/pages/OccasionCalendar.tsx
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaBell,
  FaBirthdayCake,
  FaBriefcase,
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaExternalLinkAlt,
  FaFire,
  FaGift,
  FaHeart,
  FaMapMarkedAlt,
  FaMagic,
  FaSnowflake,
  FaStar,
  FaStore,
  FaSun,
  FaTree,
  FaUsers,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import ExternalForfeoRedirectModal from '../components/ExternalForfeoRedirectModal';
import { FORFEO_GIFT_CARD_URL } from '../lib/forfeoExternalLinks';

type Occasion = {
  month: string;
  season: string;
  icon: React.ReactNode;
  title: string;
  text: string;
  tags: string[];
  cta: string;
  link: string;
};

type ProfileCard = {
  icon: React.ReactNode;
  title: string;
  text: string;
  link: string;
};

export default function OccasionCalendar() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');
  const [isRedirectOpen, setIsRedirectOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const copy = useMemo(() => ({
    seoTitle: isEn ? 'Occasion calendar | Forfeo Explore' : 'Calendrier des occasions | Forfeo Explore',
    seoDescription: isEn
      ? 'Plan local gift moments throughout the year: birthdays, holidays, couple escapes, family outings and corporate gifts.'
      : 'Planifiez des moments à offrir toute l’année : anniversaires, fêtes, escapades à deux, sorties famille et cadeaux corporatifs.',
    eyebrow: isEn ? 'Gift planning calendar' : 'Calendrier cadeau intelligent',
    title: isEn ? 'Never miss a moment worth offering.' : 'Ne manquez plus jamais un moment à offrir.',
    subtitle: isEn
      ? 'A seasonal and emotional calendar to choose the right local gift idea, then continue to forfeo.com when it is time to buy the official Forfeo virtual card.'
      : 'Un calendrier saisonnier et émotionnel pour choisir la bonne idée cadeau locale, puis poursuivre vers forfeo.com au moment d’acheter la carte virtuelle Forfeo officielle.',
    primaryCta: isEn ? 'Buy the Forfeo card' : 'Acheter la carte Forfeo',
    secondaryCta: isEn ? 'Open gift guide' : 'Voir le parcours cadeau',
    filterTitle: isEn ? 'Filter by intention' : 'Filtrer par intention',
    all: isEn ? 'All' : 'Tout',
    couple: isEn ? 'Couple' : 'Couple',
    family: isEn ? 'Family' : 'Famille',
    corporate: isEn ? 'Corporate' : 'Corporatif',
    visitor: isEn ? 'Visitors' : 'Visiteurs',
    lastMinute: isEn ? 'Last minute' : 'Dernière minute',
    seasonal: isEn ? 'Seasonal' : 'Saisonnier',
    timelineTitle: isEn ? 'A full year of local moments' : 'Une année complète de moments locaux',
    timelineText: isEn
      ? 'Use this calendar to plan campaigns, gifts, reminders and themed Forfeo moments without creating confusion between Explore and Forfeo.com.'
      : 'Utilisez ce calendrier pour planifier campagnes, cadeaux, rappels et moments Forfeo thématiques sans créer de confusion entre Explore et Forfeo.com.',
    profileTitle: isEn ? 'Quick paths by recipient' : 'Parcours rapides selon la personne',
    bridgeTitle: isEn ? 'Explore inspires. Forfeo.com handles the card purchase.' : 'Explore inspire. Forfeo.com gère l’achat de la carte.',
    bridgeText: isEn
      ? 'Forfeo Explore helps users choose the right moment. When they decide to buy a virtual card, we show a clear warning before redirecting to Forfeo.com, the official transactional site.'
      : 'Forfeo Explore aide l’utilisateur à choisir le bon moment. Quand il décide d’acheter une carte virtuelle, on affiche un avertissement clair avant de le rediriger vers Forfeo.com, le site transactionnel officiel.',
    stayExplore: isEn ? 'Stay on Explore' : 'Rester sur Explore',
    finalTitle: isEn ? 'Turn every occasion into a local experience.' : 'Transformez chaque occasion en expérience locale.',
    finalText: isEn
      ? 'Start with inspiration, choose the occasion, then continue safely to Forfeo.com when the gift card purchase is ready.'
      : 'Commencez par l’inspiration, choisissez l’occasion, puis poursuivez en toute sécurité vers Forfeo.com lorsque l’achat de la carte est prêt.',
  }), [isEn]);

  const filters = [
    { id: 'all', label: copy.all, icon: <FaStar /> },
    { id: 'couple', label: copy.couple, icon: <FaHeart /> },
    { id: 'family', label: copy.family, icon: <FaUsers /> },
    { id: 'corporate', label: copy.corporate, icon: <FaBriefcase /> },
    { id: 'visitor', label: copy.visitor, icon: <FaMapMarkedAlt /> },
    { id: 'last-minute', label: copy.lastMinute, icon: <FaClock /> },
    { id: 'seasonal', label: copy.seasonal, icon: <FaTree /> },
  ];

  const occasions: Occasion[] = [
    {
      month: isEn ? 'January' : 'Janvier',
      season: isEn ? 'Winter reset' : 'Relance hivernale',
      icon: <FaSnowflake />,
      title: isEn ? 'Fresh start & cozy escapes' : 'Nouveau départ & escapades cocooning',
      text: isEn ? 'Spas, quiet stays, restaurants and calm local moments after the holidays.' : 'Spas, séjours tranquilles, restos et moments locaux doux après les fêtes.',
      tags: ['couple', 'seasonal'],
      cta: isEn ? 'View winter ideas' : 'Voir les idées hiver',
      link: '/decouvrir-quebec',
    },
    {
      month: isEn ? 'February' : 'Février',
      season: isEn ? 'Love & connection' : 'Amour & connexion',
      icon: <FaHeart />,
      title: isEn ? 'Valentine’s Day and couple moments' : 'Saint-Valentin et moments à deux',
      text: isEn ? 'Gift a dinner, a stay, a relaxing moment or a flexible Forfeo card.' : 'Offrir un souper, un séjour, un moment détente ou une carte Forfeo flexible.',
      tags: ['couple', 'last-minute'],
      cta: isEn ? 'Gift a moment' : 'Offrir un moment',
      link: '/offrir-un-moment',
    },
    {
      month: isEn ? 'March' : 'Mars',
      season: isEn ? 'Spring break' : 'Relâche',
      icon: <FaUsers />,
      title: isEn ? 'Family outings and school break' : 'Sorties famille et semaine de relâche',
      text: isEn ? 'Ideas for families, visitors and simple discoveries close to home.' : 'Des idées pour les familles, les visiteurs et les découvertes près de chez soi.',
      tags: ['family', 'visitor'],
      cta: isEn ? 'Explore regions' : 'Explorer les régions',
      link: '/carte',
    },
    {
      month: isEn ? 'April' : 'Avril',
      season: isEn ? 'Spring renewal' : 'Renouveau printanier',
      icon: <FaTree />,
      title: isEn ? 'Easter, maple season and local discoveries' : 'Pâques, temps des sucres et découvertes locales',
      text: isEn ? 'Local food, short drives, fresh experiences and seasonal surprises.' : 'Gastronomie locale, petites routes, expériences fraîches et surprises saisonnières.',
      tags: ['family', 'seasonal'],
      cta: isEn ? 'Discover Quebec' : 'Découvrir le Québec',
      link: '/decouvrir-quebec',
    },
    {
      month: isEn ? 'May' : 'Mai',
      season: isEn ? 'Mother’s Day' : 'Fête des Mères',
      icon: <FaGift />,
      title: isEn ? 'Relaxing and thoughtful gifts' : 'Cadeaux doux et attentionnés',
      text: isEn ? 'A spa, brunch, getaway or flexible card that lets her choose.' : 'Un spa, brunch, séjour ou une carte flexible qui lui laisse le choix.',
      tags: ['family', 'last-minute'],
      cta: isEn ? 'Buy a Forfeo card' : 'Acheter une carte Forfeo',
      link: 'external-card',
    },
    {
      month: isEn ? 'June' : 'Juin',
      season: isEn ? 'Father’s Day & graduations' : 'Fête des Pères & graduations',
      icon: <FaBirthdayCake />,
      title: isEn ? 'Celebrate achievements and people' : 'Célébrer les réussites et les personnes',
      text: isEn ? 'A local experience feels more personal than a generic gift.' : 'Une expérience locale est plus personnelle qu’un cadeau générique.',
      tags: ['family', 'corporate'],
      cta: isEn ? 'Open gift guide' : 'Voir le guide cadeau',
      link: '/offrir-un-moment',
    },
    {
      month: isEn ? 'July' : 'Juillet',
      season: isEn ? 'Summer travel' : 'Vacances d’été',
      icon: <FaSun />,
      title: isEn ? 'Terraces, road trips and Quebec visitors' : 'Terrasses, escapades et visiteurs au Québec',
      text: isEn ? 'Help travelers discover Quebec before and during their stay.' : 'Aider les voyageurs à découvrir le Québec avant et pendant leur séjour.',
      tags: ['visitor', 'seasonal'],
      cta: isEn ? 'Open Quebec guide' : 'Voir le guide Québec',
      link: '/decouvrir-quebec',
    },
    {
      month: isEn ? 'August' : 'Août',
      season: isEn ? 'Late summer' : 'Fin d’été',
      icon: <FaFire />,
      title: isEn ? 'Last Call moments before routine returns' : 'Moments Last Call avant le retour à la routine',
      text: isEn ? 'Flash opportunities, quick stays and spontaneous local plans.' : 'Occasions flash, courts séjours et plans locaux spontanés.',
      tags: ['last-minute', 'seasonal'],
      cta: isEn ? 'See Last Call' : 'Voir Last Call',
      link: '/last-call',
    },
    {
      month: isEn ? 'September' : 'Septembre',
      season: isEn ? 'Recognition' : 'Reconnaissance',
      icon: <FaBriefcase />,
      title: isEn ? 'Employee, client and partner gifts' : 'Cadeaux employés, clients et partenaires',
      text: isEn ? 'A polished local gift for teams, referrals and business relationships.' : 'Un cadeau local soigné pour équipes, références et relations d’affaires.',
      tags: ['corporate'],
      cta: isEn ? 'Gift a card' : 'Offrir une carte',
      link: 'external-card',
    },
    {
      month: isEn ? 'October' : 'Octobre',
      season: isEn ? 'Fall colors' : 'Couleurs d’automne',
      icon: <FaTree />,
      title: isEn ? 'Romantic and scenic fall escapes' : 'Escapades romantiques et routes d’automne',
      text: isEn ? 'A perfect moment for stays, food routes, vineyards and cozy weekends.' : 'Le moment parfait pour séjours, routes gourmandes, vignobles et weekends cosy.',
      tags: ['couple', 'visitor', 'seasonal'],
      cta: isEn ? 'Explore fall ideas' : 'Voir les idées automne',
      link: '/decouvrir-quebec',
    },
    {
      month: isEn ? 'November' : 'Novembre',
      season: isEn ? 'Early gifting' : 'Préparation cadeaux',
      icon: <FaBell />,
      title: isEn ? 'Plan holiday gifts before the rush' : 'Préparer les cadeaux avant la cohue',
      text: isEn ? 'Build reminders, lists and simple digital gifts before December.' : 'Créer des rappels, listes et cadeaux numériques simples avant décembre.',
      tags: ['last-minute', 'corporate'],
      cta: isEn ? 'Buy a Forfeo card' : 'Acheter une carte Forfeo',
      link: 'external-card',
    },
    {
      month: isEn ? 'December' : 'Décembre',
      season: isEn ? 'Holiday season' : 'Temps des fêtes',
      icon: <FaGift />,
      title: isEn ? 'Holiday gifts that can still feel personal' : 'Cadeaux des fêtes qui restent personnels',
      text: isEn ? 'A digital card for last-minute gifts, families, teams and visitors.' : 'Une carte numérique pour cadeaux de dernière minute, familles, équipes et visiteurs.',
      tags: ['family', 'corporate', 'last-minute'],
      cta: isEn ? 'Continue to Forfeo.com' : 'Continuer vers Forfeo.com',
      link: 'external-card',
    },
  ];

  const filteredOccasions = activeFilter === 'all'
    ? occasions
    : occasions.filter((occasion) => occasion.tags.includes(activeFilter));

  const profiles: ProfileCard[] = [
    { icon: <FaHeart />, title: isEn ? 'For couples' : 'Pour les couples', text: isEn ? 'Spa, dinner, stay, surprise or romantic weekend.' : 'Spa, souper, séjour, surprise ou weekend romantique.', link: '/offrir-un-moment' },
    { icon: <FaUsers />, title: isEn ? 'For families' : 'Pour les familles', text: isEn ? 'Simple outings, school breaks, birthdays and discoveries.' : 'Sorties simples, relâche, anniversaires et découvertes.', link: '/decouvrir-quebec' },
    { icon: <FaBriefcase />, title: isEn ? 'For companies' : 'Pour entreprises', text: isEn ? 'Employee, client and partner gifts with a local premium feel.' : 'Cadeaux employés, clients et partenaires avec une touche locale premium.', link: 'external-card' },
    { icon: <FaMapMarkedAlt />, title: isEn ? 'For visitors' : 'Pour visiteurs', text: isEn ? 'Give them a reason to discover Quebec before arrival.' : 'Leur donner une raison de découvrir le Québec avant l’arrivée.', link: '/decouvrir-quebec' },
  ];

  const handleOccasionClick = (link: string) => {
    if (link === 'external-card') {
      setIsRedirectOpen(true);
      return;
    }
    navigate(link);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 font-sans overflow-hidden">
      <Header />
      <SeoHead title={copy.seoTitle} description={copy.seoDescription} canonicalPath="/calendrier-des-occasions" />
      <ExternalForfeoRedirectModal
        isOpen={isRedirectOpen}
        onClose={() => setIsRedirectOpen(false)}
        targetUrl={FORFEO_GIFT_CARD_URL}
        contextLabel="forfeo.com/carte-cadeau"
      />

      <main className="pt-24">
        <section className="relative overflow-hidden bg-slate-950 px-4 py-20 text-white md:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_14%,rgba(236,72,153,0.34),transparent_30%),radial-gradient(circle_at_88%_12%,rgba(56,189,248,0.28),transparent_32%),linear-gradient(135deg,#020617,#0f172a_48%,#1e1b4b)]" />
          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:34px_34px]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.04fr_0.96fr]">
            <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-pink-100 backdrop-blur-md">
                <FaCalendarAlt className="text-pink-300" /> {copy.eyebrow}
              </div>
              <h1 className="max-w-5xl text-5xl font-black leading-[0.92] tracking-tighter md:text-7xl lg:text-8xl">{copy.title}</h1>
              <p className="mt-7 max-w-2xl text-base font-semibold leading-relaxed text-slate-300 md:text-xl">{copy.subtitle}</p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => setIsRedirectOpen(true)} className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-7 py-4 text-xs font-black uppercase tracking-widest text-slate-950 shadow-2xl transition hover:-translate-y-0.5 hover:bg-pink-50">
                  {copy.primaryCta} <FaExternalLinkAlt />
                </button>
                <Link to="/offrir-un-moment" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:-translate-y-0.5 hover:bg-white/15">
                  {copy.secondaryCta} <FaArrowRight />
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-3 text-[11px] font-black uppercase tracking-widest text-slate-300">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2"><FaCheck className="text-green-300" /> {isEn ? 'No duplicate checkout' : 'Pas de checkout doublé'}</span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2"><FaCheck className="text-green-300" /> forfeo.com</span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2"><FaCheck className="text-green-300" /> {isEn ? 'Return to Explore anytime' : 'Retour Explore en tout temps'}</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
              <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-pink-500/25 via-blue-400/20 to-cyan-300/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-[3rem] border border-white/10 bg-white/10 p-5 shadow-2xl backdrop-blur-md">
                <div className="rounded-[2.5rem] bg-white p-6 text-slate-950 shadow-xl">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Forfeo</div>
                      <div className="mt-1 text-2xl font-black tracking-tight">{isEn ? 'Occasion planner' : 'Planificateur d’occasions'}</div>
                    </div>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-2xl text-white"><FaCalendarAlt /></div>
                  </div>
                  <div className="grid gap-3">
                    {occasions.slice(0, 4).map((occasion) => (
                      <button key={occasion.month} onClick={() => handleOccasionClick(occasion.link)} className="group flex items-center gap-4 rounded-3xl border border-slate-100 bg-slate-50 p-4 text-left transition hover:-translate-y-0.5 hover:bg-white hover:shadow-lg">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm">{occasion.icon}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{occasion.month}</div>
                          <div className="truncate text-sm font-black text-slate-950">{occasion.title}</div>
                        </div>
                        <FaArrowRight className="text-slate-300 transition group-hover:translate-x-1 group-hover:text-blue-600" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 md:py-20">
          <div className="mb-7 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-blue-700"><FaBell /> {copy.filterTitle}</div>
              <h2 className="text-4xl font-black tracking-tight text-slate-950 md:text-5xl">{copy.timelineTitle}</h2>
              <p className="mt-4 max-w-3xl text-base font-semibold leading-relaxed text-slate-600">{copy.timelineText}</p>
            </div>
          </div>

          <div className="mb-8 flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`shrink-0 inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest transition ${
                  activeFilter === filter.id
                    ? 'bg-slate-950 text-white shadow-lg shadow-slate-900/20'
                    : 'bg-white text-slate-600 border border-slate-100 hover:bg-slate-50'
                }`}
              >
                {filter.icon} {filter.label}
              </button>
            ))}
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredOccasions.map((occasion, index) => (
              <motion.div
                key={`${occasion.month}-${occasion.title}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: Math.min(index * 0.03, 0.2) }}
                className="group flex min-h-[280px] flex-col rounded-[2.25rem] border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-2xl"
              >
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-2xl text-white shadow-lg">{occasion.icon}</div>
                  <div className="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">{occasion.month}</div>
                </div>
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-600">{occasion.season}</div>
                <h3 className="mt-2 text-2xl font-black leading-tight text-slate-950">{occasion.title}</h3>
                <p className="mt-4 flex-1 text-sm font-semibold leading-relaxed text-slate-600">{occasion.text}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {occasion.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-slate-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">{filters.find((f) => f.id === tag)?.label || tag}</span>
                  ))}
                </div>
                <button onClick={() => handleOccasionClick(occasion.link)} className="mt-6 inline-flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-5 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:bg-blue-700">
                  {occasion.cta} {occasion.link === 'external-card' ? <FaExternalLinkAlt /> : <FaArrowRight />}
                </button>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-white px-4 py-18 md:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-50 text-pink-600"><FaGift /></div>
              <div>
                <h2 className="text-3xl font-black tracking-tight text-slate-950 md:text-5xl">{copy.profileTitle}</h2>
                <p className="mt-2 text-sm font-semibold text-slate-500">{isEn ? 'Choose the person, then let Forfeo guide the moment.' : 'Choisissez la personne, puis laissez Forfeo guider le moment.'}</p>
              </div>
            </div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {profiles.map((profile) => (
                <button key={profile.title} onClick={() => handleOccasionClick(profile.link)} className="group rounded-[2rem] border border-slate-100 bg-slate-50 p-6 text-left transition hover:-translate-y-1 hover:bg-white hover:shadow-xl">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl text-slate-950 shadow-sm">{profile.icon}</div>
                  <h3 className="text-lg font-black text-slate-950">{profile.title}</h3>
                  <p className="mt-3 text-sm font-semibold leading-relaxed text-slate-600">{profile.text}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-blue-600">{isEn ? 'Continue' : 'Continuer'} <FaArrowRight className="transition group-hover:translate-x-1" /></div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="rounded-[2.5rem] bg-slate-950 p-8 text-white shadow-2xl md:p-10">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-cyan-100"><FaStore /> Forfeo.com</div>
              <h2 className="text-4xl font-black tracking-tight md:text-5xl">{copy.bridgeTitle}</h2>
              <p className="mt-5 text-base font-semibold leading-relaxed text-slate-300">{copy.bridgeText}</p>
            </div>
            <div className="grid gap-4">
              {[
                isEn ? 'Use the calendar to choose the right moment.' : 'Utilisez le calendrier pour choisir le bon moment.',
                isEn ? 'Stay on Explore to compare regions, ideas and experiences.' : 'Restez sur Explore pour comparer régions, idées et expériences.',
                isEn ? 'Continue to Forfeo.com only when the official virtual card purchase is ready.' : 'Poursuivez vers Forfeo.com seulement lorsque l’achat officiel de la carte virtuelle est prêt.',
                isEn ? 'Return to Explore anytime to plan how the gift could be used.' : 'Revenez sur Explore en tout temps pour planifier comment le cadeau pourrait être utilisé.',
              ].map((step, index) => (
                <div key={step} className="flex items-start gap-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black text-white">{index + 1}</div>
                  <p className="pt-2 text-sm font-bold leading-relaxed text-slate-700">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-24">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[3rem] bg-gradient-to-br from-pink-600 via-slate-950 to-blue-700 p-8 text-center text-white shadow-2xl md:p-14">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-3xl bg-white/15 text-3xl backdrop-blur-md"><FaCalendarAlt /></div>
            <h2 className="mx-auto max-w-3xl text-4xl font-black tracking-tight md:text-6xl">{copy.finalTitle}</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base font-semibold leading-relaxed text-blue-100 md:text-lg">{copy.finalText}</p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <button onClick={() => setIsRedirectOpen(true)} className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:-translate-y-0.5 hover:bg-blue-50">
                {copy.primaryCta} <FaExternalLinkAlt />
              </button>
              <Link to="/offrir-un-moment" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-8 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:-translate-y-0.5 hover:bg-white/15">
                {copy.secondaryCta} <FaArrowRight />
              </Link>
              <Link to="/revelation-surprise" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-fuchsia-300/30 bg-fuchsia-400/15 px-8 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:-translate-y-0.5 hover:bg-fuchsia-400/25">
                {isEn ? 'Surprise reveal' : 'Révélation surprise'} <FaMagic />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
